import React, { useEffect, useRef, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { Box, Typography } from "@mui/material";
import { toast } from "react-toastify";
import useFirebase from "../../commons/firebase/useFirebase";

interface IEditor {
	onChange: (html: string, textPlain?: string) => void;
	initialText?: string;
	label?: string;
	idRef: string;
}

const CustomEditor: React.FC<IEditor> = ({
	onChange,
	initialText = "",
	label,
	idRef,
}) => {
	const editorRef = useRef<any>(null);
	const { uploadPicture, deletePicture } = useFirebase();
	const [imageMappings, setImageMappings] = useState<Map<string, string>>(
		new Map()
	);

	// Extraer imágenes y sus rutas del initialText
	useEffect(() => {
		if (initialText) {
			const doc = new DOMParser().parseFromString(
				initialText,
				"text/html"
			);
			const images = doc.querySelectorAll("img");
			const initialMappings = new Map<string, string>();
			images.forEach((img) => {
				const src = img.getAttribute("src");
				const alt = img.getAttribute("alt");
				if (src && alt) {
					initialMappings.set(src, alt);
				}
			});
			setImageMappings(initialMappings);
		}
	}, [initialText]);

	const handleFileUpload = async (file: File, callback: any) => {
		const STORAGE_PATH = `/content/${idRef}/${file.name}`;

		await uploadPicture(STORAGE_PATH, file, (success, url) => {
			if (success) {
				// Almacena la URL y la ruta de referencia en el estado
				setImageMappings((prev) =>
					new Map(prev).set(url, STORAGE_PATH)
				);
				callback(url, {
					alt: STORAGE_PATH,
					title: file.name,
					width: "100%",
					height: "auto",
				});
			} else {
				toast.error("No pudimos cargar la imagen");
			}
		});
	};

	const handleChange = (content: any) => {
		if (editorRef.current) {
			const doc = new DOMParser().parseFromString(content, "text/html");
			const textPlain = doc.body.textContent || "";
			onChange(content, textPlain);

			// Utiliza Array.from para convertir NodeListOf a un Array
			const imageElements = Array.from(doc.querySelectorAll("img"));
			const currentImages = new Set(imageElements.map((img) => img.src));

			// Determina qué imágenes han sido eliminadas
			const imagesToDelete = new Map(imageMappings);
			currentImages.forEach((url) => imagesToDelete.delete(url));

			// Elimina las imágenes borradas de Firebase
			imagesToDelete.forEach((storagePath, url) => {
				deletePicture(storagePath, (res: boolean) => {
					if (!res)
						console.log("No se están eliminando las imágenes");
				});
				// Elimina la entrada del mapeo
				setImageMappings((prev) => {
					const newMap = new Map(prev);
					newMap.delete(url);
					return newMap;
				});
			});
		}
	};

	return (
		<div style={{ marginBlock: "15px" }}>
			{label && (
				<>
					<Typography>{label}</Typography>
					<Box sx={{ mb: 1 }} />
				</>
			)}
			<Editor
				apiKey="229talfvzzhmp7d3ihkgxkbag3bwnq47urwa4mvsldfsy21t"
				onInit={(evt, editor) => (editorRef.current = editor)}
				initialValue={initialText}
				onEditorChange={handleChange}
				init={{
					placeholder: "Sobre que nos quieres contar...",
					height: 500,
					menubar: true,
					plugins:
						"advlist autolink lists link image charmap print preview anchor searchreplace visualblocks code fullscreen insertdatetime media table lists paste code wordcount",
					toolbar:
						"link | styles | bold italic | alignleft aligncenter alignright alignjustify | outdent indent | numlist bullist | forecolor backcolor emoticons",
					content_style: `body { font-family:Helvetica,Arial,sans-serif; font-size:14px } 
                         a {color: #35A8E0}
                        `,
					image_title: true,
					automatic_uploads: false,
					file_picker_types: "image",
					paste_data_images: false,
					file_picker_callback: function (callback, value, meta) {
						if (meta.filetype === "image") {
							var input = document.createElement("input");
							input.setAttribute("type", "file");
							input.setAttribute("accept", "image/*");
							input.onchange = async (e: any) => {
								var file = e.target.files[0];
								await handleFileUpload(
									file,
									(url: string, details: any) => {
										callback(url, details);
									}
								);
							};
							input.click();
						}
					},
				}}
			/>
		</div>
	);
};

export default CustomEditor;
